import Loader from "common/components/Loader";
import { useCampaignContext } from "common/helpers/CampaignContext";
import { useSelectedWorkspaceContext } from "common/helpers/SelectedWorkspaceContext";
import type { CampaignType, ProfileSearchParams } from "common/types";

import useTargeting from "../../../datahooks/useTargeting";
import Crm from "./Crm";
import Drip from "./Drip";
import GroupAndEvent from "./GroupAndEvent";
import List from "./List";
import Navigator from "./Navigator";
import Post from "./Post";
import ReconnectAccount from "./ReconnectAccount";
import Search from "./Search";

import type React from "react";
import type {
  DripTargeting,
  EventTargeting,
  GroupTargeting,
  NavigatorFiltersTargeting,
  PostTargeting,
} from "../../../types";

export default function Targeting({
  openLeadPreview,
}: {
  openLeadPreview: (leadId: string) => void;
}) {
  const { accounts } = useSelectedWorkspaceContext();
  const { miner, type, outreach_type: outreachType } = useCampaignContext();
  const { targeting, isLoadingTargeting } = useTargeting();

  const minerAccount = accounts.find(({ id }) => id === miner);
  const isDrip = outreachType === "drip";
  const isList = type === "list" || type === "crm";

  // Mining can't work If the miner account is logged out or removed (this is not relevant for "list" campaigns)
  if (!isList && (!minerAccount || minerAccount.state !== "LoggedIn")) {
    return <ReconnectAccount account={minerAccount} isDrip={isDrip} />;
  }

  if (isLoadingTargeting) {
    return <Loader />;
  }

  const targetingSetup: Record<CampaignType, React.ReactNode> = {
    search: <Search initialParams={targeting as ProfileSearchParams} />,
    event: (
      <GroupAndEvent
        type="event"
        initialId={(targeting as EventTargeting)?.event_id}
      />
    ),
    group: (
      <GroupAndEvent
        type="group"
        initialId={(targeting as GroupTargeting)?.group_id}
      />
    ),
    post: <Post initialPost={targeting as PostTargeting} />,
    list: <List />,
    drip: (
      <Drip
        initialFilters={targeting as DripTargeting}
        openLeadPreview={openLeadPreview}
      />
    ),
    navigator: (
      <Navigator initialTargeting={targeting as NavigatorFiltersTargeting} />
    ),
    crm: <Crm />,
  };

  return targetingSetup[type];
}
